import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Loading,
  Paper,
  Switch,
  Tab,
  TabPanel,
  Tabs,
  TextField,
  Typography,
} from "../../../components";
import { InputErros, PermisoesHelper } from "../../../helpers";
import { AddLayout } from "../../../layouts";
import { formatPrice, responseErros, userLogs, voltar } from "../../../utils";
import {
  EtiquetasService,
  notification,
  SessionService,
} from "../../../services";
import { Etiqueta } from "../entities";
import { DesignEtiquetaTab, PreviewEtiquetaTab } from "./tabs";
import { useImportContext } from "../../../contexts/import.context";
import etiquetasValidator from "./middlewares/etiquetas.validator";
import JsBarcode from "jsbarcode";

const FONT_SPECS = {
  1: 21,
  2: 25,
  3: 29,
  4: 34,
  5: 77,
};

const TYPECODE = {
  1: "CODE128",
  3: "CODE39",
  E30: "EAN13",
  E80: "EAN8",
};

const AddEtiquestasView = ({ match }) => {
  const { id } = match.params;
  function tabProps(index) {
    return {
      id: `full-width-tab-${index}`,
      "aria-controls": `full-width-tabpanel-${index}`,
    };
  }

  let etiquetaEntity = new Etiqueta({
    ativado: true,
    larguraFolha: 85,
    alturaEtiqueta: 30,
    margemBordaEtiqueta: 1,
    margemMeioEtiqueta: 3,
    colunasEtiqueta: 2,
    layout: [],
  });

  const { initialCollapses } = useImportContext();
  const [etiqueta, setEtiqueta] = useState(etiquetaEntity);
  const [productData, setProdutoData] = useState({
    DESCRICAO: "Produto Exemplo",
    COD: "PRD001",
    PRECO: "29.90",
    CODBAR: 1234567891233,
  });
  const availableElements = [
    {
      elemento: "DESCRICAO",
      content: "Descrição do Produto",
      fontSize: 2,
    },
    {
      elemento: "COD",
      content: "Código do Produto",
      fontSize: 3,
    },
    {
      elemento: "PRECO",
      content: "Preço",
      fontSize: 5,
    },
    {
      elemento: "CODBAR",
      content: "Código de Barras",
      fontSize: 3,
      codeType: "E30",
      codeHeight: 50,
      codeWidth: 2,
      showLabelCode: true,
    },
  ];
  const [labelElements, setLabelElements] = useState([]);
  const [abaValue, setAbaValue] = useState(0);
  const [userLog, setUserLog] = useState({});
  const [loading, setLoading] = useState(false);
  const [configuracoes, setConfiguracoes] = useState({});
  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);
  const etiquetasService = new EtiquetasService();
  const sessionService = new SessionService();
  const permissoesHelper = new PermisoesHelper();
  const dadosEmpresa = sessionService.getEmpresaAndUsuario();

  const AtivadoComponent = ({ etiqueta, setEtiqueta }) => {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={etiqueta?.ativado ? etiqueta.ativado : false}
            onChange={() =>
              setEtiqueta((etiqueta) => {
                return {
                  ...etiqueta,
                  ativado: !etiqueta?.ativado,
                };
              })
            }
            name="ativado"
            color="primary"
          />
        }
        label={etiqueta?.ativado ? "Ativado" : "Desativado"}
      />
    );
  };

  const onClickLog = async () => {
    const response = await userLogs(id, "etiquetas");
    if (!response.isAxiosError) {
      setUserLog(response.data);
    } else responseErros(response);
  };

  useEffect(() => {
    buscarEtiqueta();
    buscarConfiguracao();
  }, []);

  const buscarConfiguracao = () => {
    if (dadosEmpresa.empresa?.configuracoes) {
      const data = dadosEmpresa.empresa.configuracoes;
      setConfiguracoes(data);
    }
  };

  const buscarEtiqueta = async () => {
    if (!id) return;
    setLoading(true);
    const result = await etiquetasService.getById(id);
    if (!result.isAxiosError) {
      etiquetaEntity = new Etiqueta(result.data, id);
      setLabelElements(etiquetaEntity.layout);
      setEtiqueta(etiquetaEntity);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEtiqueta((prevState) => ({ ...prevState, [name]: value }));
    if (name !== "descricao") setLabelElements([]);
  };

  function mmToPx(mm, dpi = 203) {
    return (mm * dpi) / 25.4;
  }

  function getEplFontSize(fontValue, dpi = 203) {
    const font = FONT_SPECS[fontValue];
    if (!font) throw new Error("Fonte inválida (use 1 a 5)");
    const mmPorDot = 25.4 / dpi;
    const alturaMm = font * mmPorDot;
    const alturaPx = mmToPx(alturaMm);
    return alturaPx.toFixed(2);
  }

  const dimensoes = useMemo(() => {
    const alturaEtiqueta = parseFloat(etiqueta?.alturaEtiqueta) || 30;
    const larguraFolha = parseFloat(etiqueta?.larguraFolha) || 102;
    const colunasEtiqueta = parseInt(etiqueta?.colunasEtiqueta) || 1;

    const margemLateral = parseFloat(etiqueta?.margemBordaEtiqueta) || 1;
    const margemMeio =
      parseFloat(etiqueta?.margemMeioEtiqueta) || margemLateral;

    const larguraEtiqueta = Math.floor(
      larguraFolha - margemMeio * (colunasEtiqueta + 1)
    );

    const larguraEtiquetaPx = mmToPx(larguraEtiqueta);
    const alturaEtiquetaPx = mmToPx(alturaEtiqueta);
    const margemLateralPx = mmToPx(margemLateral);
    const margemMeioPx = mmToPx(margemMeio);

    return {
      larguraEtiquetaPx,
      alturaEtiquetaPx,
      margemLateralPx,
      margemMeioPx,
    };
  }, [
    etiqueta?.alturaEtiqueta,
    etiqueta?.larguraFolha,
    etiqueta?.colunasEtiqueta,
    etiqueta?.margemBordaEtiqueta,
    etiqueta?.margemMeioEtiqueta,
  ]);

  const renderText = (fontSize, descricao, type) => {
    function formatValue(value) {
      let string = value;
      if (fontSize === 5) {
        string = value.toString().toUpperCase();
      }
      if (type == "PRECO") {
        string = "R$" + formatPrice(parseFloat(string), false);
      }
      return string;
    }
    return formatValue(String(descricao));
  };

  async function handleSubmit() {
    const etiquetaDto = new Etiqueta(etiqueta, id);
    etiquetaDto.createLayout(labelElements);
    setLoading(true);
    etiquetasValidator
      .validate(etiquetaDto, { abortEarly: false })
      .then(async () => {
        if (id) {
          const result = await etiquetasService.atualizar(id, etiquetaDto);
          if (!result.isAxiosError) {
            notification.alteracaoSucesso();
            initialCollapses();
            voltar();
          } else {
            responseErros(result);
          }
        } else {
          const result = await etiquetasService.cadastrar(etiquetaDto);
          if (!result.isAxiosError) {
            notification.cadastroSucesso();
            initialCollapses();
            voltar();
          } else {
            responseErros(result);
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        inputErros.set(err);
      });
  }

  async function print() {
    if (!id) return;
    setLoading(true);
    const result = await etiquetasService.buscarModeloBase(id, productData);
    if (!result.isAxiosError) {
      const formData = new FormData();
      const buffer = new Uint8Array(result.data.data);
      const blob = new Blob([buffer], { type: "text/plain;charset=utf-8" });
      formData.append("files", blob);
      formData.append("printerName", configuracoes?.impressoraEtiqueta || null);
      const resultImpressao = await etiquetasService.imprimir(formData);
      if (!resultImpressao.isAxiosError) {
        notification.sucessoGenericos("Impressão realizada com sucesso!");
      } else {
        responseErros(resultImpressao);
      }
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  function BarcodePreview({
    value,
    type = "EAN13",
    height = 100,
    width = 2,
    fontSize,
    showValue = true,
    onContextMenu,
  }) {
    const widthBar = width - 0.1;
    const svgRef = useRef(null);

    useEffect(() => {
      if (!value) return;
      let validValue = value;

      if (type === "EAN13") {
        validValue = String(value).replace(/\D/g, "");
        if (validValue.length > 12) validValue = validValue.slice(0, 12);
        if (validValue.length < 12) validValue = validValue.padStart(12, "0");
      } else if (type === "EAN8") {
        validValue = String(value).replace(/\D/g, "");
        if (validValue.length > 7) validValue = validValue.slice(0, 7);
        if (validValue.length < 7) validValue = validValue.padStart(7, "0");
      }

      try {
        JsBarcode(svgRef.current, validValue, {
          format: type,
          height,
          width: widthBar,
          displayValue: false,
          margin: 0,
          marginBottom: type === "EAN13" ? -12 : 0,
          lineColor: "#000",
        });
      } catch (err) {
        console.warn("Erro ao gerar código de barras:", err.message);
      }
    }, [value, type, height, width, fontSize]);

    return (
      <div
        onContextMenu={onContextMenu ? onContextMenu : null}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <svg
          ref={svgRef}
          style={{
            height: `${height}px`,
            width: `${widthBar}px`,
          }}
        />
        {showValue && (
          <div
            style={{
              fontSize: `${fontSize - 8}px`,
              fontFamily: " monospace",
              fontWeight: 600,
            }}
          >
            {value}
          </div>
        )}
      </div>
    );
  }

  function createItemEtiqueta(element) {
    const newLabel = {
      ...element,
      x: 15,
      y: 15,
      fontSize: element.fontSize ?? 3,
    };
    setLabelElements((prev) => {
      const exists = prev.some((label) => label.elemento === newLabel.elemento);
      if (exists) {
        return prev.map((label) =>
          label.elemento === newLabel.elemento
            ? { ...newLabel, x: label.x, y: label.y }
            : label
        );
      }
      return [...prev, newLabel];
    });
  }

  return (
    <AddLayout
      id={id}
      title={etiqueta?.id ? "Editar Etiqueta" : "Cadastrar Etiqueta"}
      onClickSalvar={() => handleSubmit()}
      loading={loading}
      userLog={etiqueta?.id ? userLog : null}
      onClickLog={() => onClickLog()}
      showUpdate={permissoesHelper.temPermisao("meios-pagamento-editar")}
      submitSimples
      actions={
        <AtivadoComponent etiqueta={etiqueta} setEtiqueta={setEtiqueta} />
      }
      actionsTotalizadores={
        <div className="w-100 d-flex justify-content-end">
          <Button variant="contained" color="primary" onClick={() => print()}>
            <i
              className="ph-fill ph-printer"
              style={{ fontSize: 22, marginRight: 10, color: "white" }}
            ></i>
            TESTE DE IMPRESSÃO
          </Button>
        </div>
      }
    >
      <Grid container>
        <Grid item xs={12}>
          <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
            {" "}
            Identificação da Etiqueta
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                id="descricao"
                name="descricao"
                label="Descrição"
                variant="outlined"
                margin="normal"
                value={etiqueta?.descricao ? etiqueta.descricao : ""}
                onChange={(e) => handleInputChange(e)}
                fullWidth
                required
                error={inputErros.get("descricao")}
              />
            </Grid>
          </Grid>
        </Grid>
        <Divider sx={{ mt: 2, border: "1px solid grey", width: "100%" }} />
        <Grid item xs={12}>
          <Typography variant="body2" gutterBottom sx={{ mb: 2, mt: 2 }}>
            {" "}
            Parâmetros da Etiqueta
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                id="larguraFolha"
                name="larguraFolha"
                label="Largura da Folha"
                variant="outlined"
                margin="normal"
                type="number"
                disableNumberArrows
                InputProps={{
                  inputProps: { min: 0.01, step: 0.01 },
                  endAdornment: (
                    <InputAdornment position="end">mm</InputAdornment>
                  ),
                }}
                value={etiqueta?.larguraFolha ? etiqueta.larguraFolha : ""}
                onChange={(e) => {
                  if (e.target.value <= 0) e.target.value = "";
                  e.target.value = Number(e.target.value);
                  handleInputChange(e);
                }}
                fullWidth
                required
                error={inputErros.get("larguraFolha")}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                id="alturaEtiqueta"
                name="alturaEtiqueta"
                label="Altura da Etiqueta"
                variant="outlined"
                margin="normal"
                type="number"
                disableNumberArrows
                InputProps={{
                  inputProps: { min: 0.01, step: 0.01 },
                  endAdornment: (
                    <InputAdornment position="end">mm</InputAdornment>
                  ),
                }}
                value={etiqueta?.alturaEtiqueta ? etiqueta.alturaEtiqueta : ""}
                onChange={(e) => {
                  if (e.target.value <= 0) e.target.value = "";
                  e.target.value = Number(e.target.value);
                  handleInputChange(e);
                }}
                fullWidth
                required
                error={inputErros.get("alturaEtiqueta")}
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                id="margemBordaEtiqueta"
                name="margemBordaEtiqueta"
                label="Margem da Etiqueta"
                variant="outlined"
                margin="normal"
                type="number"
                disableNumberArrows
                InputProps={{
                  inputProps: { min: 0.01, step: 0.01 },
                  endAdornment: (
                    <InputAdornment position="end">mm</InputAdornment>
                  ),
                }}
                value={
                  etiqueta?.margemBordaEtiqueta
                    ? etiqueta.margemBordaEtiqueta
                    : ""
                }
                onChange={(e) => {
                  if (e.target.value <= 0) e.target.value = "";
                  e.target.value = Number(e.target.value);
                  handleInputChange(e);
                }}
                fullWidth
                required
                error={inputErros.get("margemBordaEtiqueta")}
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                id="margemMeioEtiqueta"
                name="margemMeioEtiqueta"
                label="Margem do Meio"
                variant="outlined"
                margin="normal"
                type="number"
                disableNumberArrows
                InputProps={{
                  inputProps: { min: 0.01, step: 0.01 },
                  endAdornment: (
                    <InputAdornment position="end">mm</InputAdornment>
                  ),
                }}
                value={
                  etiqueta?.margemMeioEtiqueta
                    ? etiqueta.margemMeioEtiqueta
                    : ""
                }
                onChange={(e) => {
                  if (e.target.value <= 0) e.target.value = "";
                  e.target.value = Number(e.target.value);
                  handleInputChange(e);
                }}
                fullWidth
                required
                error={inputErros.get("margemMeioEtiqueta")}
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                id="colunasEtiqueta"
                name="colunasEtiqueta"
                label="Qtd de Colunas"
                variant="outlined"
                margin="normal"
                type="number"
                disableNumberArrows
                InputProps={{
                  inputProps: { min: 1, step: 1 },
                }}
                value={
                  etiqueta?.colunasEtiqueta ? etiqueta.colunasEtiqueta : ""
                }
                onChange={(e) => {
                  if (e.target.value <= 0) e.target.value = "";
                  e.target.value = Math.trunc(Number(e.target.value));
                  handleInputChange(e);
                }}
                fullWidth
                required
                error={inputErros.get("colunasEtiqueta")}
              />
            </Grid>
          </Grid>
        </Grid>
        <Divider sx={{ mt: 2, border: "1px solid grey", width: "100%" }} />
        <Grid item xs={12}>
          <Typography variant="body2" gutterBottom sx={{ mb: 4, mt: 2 }}>
            Montagem e Pré Visualização - Folha ({etiqueta?.larguraFolha || 102}
            mm x {etiqueta?.alturaEtiqueta || 30}mm)
          </Typography>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              gutterBottom
              style={{ fontWeight: "bold" }}
            >
              Elementos Disponíveis
            </Typography>
            <Typography
              variant="caption"
              style={{
                color: "#666",
                marginBottom: "10px",
                display: "block",
              }}
            >
              Clique nos elementos para adicionar a etiqueta
            </Typography>
            <div
              className="d-flex gap-3 p-2"
              style={{
                border: "2px dashed #ddd",
                borderRadius: "8px",
                backgroundColor: "white",
                transition: "all 0.2s ease",
              }}
            >
              {availableElements.map((available, index) => (
                <div
                  key={index}
                  className="w-100 d-flex align-items-center gap-2 py-3 px-3"
                  style={{
                    backgroundColor: "white",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                    transform: "none",
                    transition: "all 0.2s ease",
                  }}
                >
                  <p className="m-0 me-auto text-uppercase fw-semibold">
                    {available.content}
                  </p>
                  <div className="d-flex gap-2">
                    <Button
                      variant="contained"
                      size="small"
                      disabled={labelElements.some(
                        (lab) => lab.elemento === available.elemento
                      )}
                      sx={{
                        height: "30px",
                        borderRadius: "20%",
                        minWidth: "18px",
                        boxShadow: "none",
                      }}
                      onClick={() => {
                        createItemEtiqueta(available);
                      }}
                    >
                      <i
                        className="ph-bold ph-plus"
                        style={{ color: "#fff", fontSize: 15 }}
                      ></i>
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      sx={{
                        height: "30px",
                        borderRadius: "20%",
                        minWidth: "18px",
                        boxShadow: "none",
                      }}
                      onClick={() => {
                        setLabelElements((prev) =>
                          prev.filter((p) => p.elemento !== available.elemento)
                        );
                      }}
                    >
                      <i
                        className="ph-bold ph-trash"
                        style={{ color: "#fff", fontSize: 15 }}
                      ></i>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Grid>
          <Grid item xs={12}>
            <Paper>
              <Tabs
                value={abaValue}
                onChange={(e, value) => {
                  e.persist();
                  setAbaValue(value);
                }}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
              >
                <Tab label="DESIGN" {...tabProps(0)} />
                <Tab label="PREVIEW" {...tabProps(1)} />
              </Tabs>
            </Paper>
            <TabPanel value={abaValue} index={0}>
              <DesignEtiquetaTab
                etiquetaEntity={etiquetaEntity}
                etiqueta={etiqueta}
                dimensoes={dimensoes}
                getEplFontSize={getEplFontSize}
                mmToPx={mmToPx}
                produto={productData}
                setProduto={setProdutoData}
                renderText={renderText}
                labelElements={labelElements}
                setLabelElements={setLabelElements}
                BarcodePreview={BarcodePreview}
                TYPECODE={TYPECODE}
              />
            </TabPanel>
            <TabPanel value={abaValue} index={1}>
              <PreviewEtiquetaTab
                etiqueta={etiqueta}
                produto={productData}
                dimensoes={dimensoes}
                mmToPx={mmToPx}
                labelElements={labelElements}
                getEplFontSize={getEplFontSize}
                renderText={renderText}
                BarcodePreview={BarcodePreview}
                TYPECODE={TYPECODE}
              />
            </TabPanel>
          </Grid>
        </Grid>
      </Grid>
      <Loading loading={loading} />
    </AddLayout>
  );
};

export default AddEtiquestasView;
